import PropTypes from "prop-types";
import React, { useState } from "react";
import { normalizeProfileSync } from "./profileSyncContract";

const decisionLabel = (action) => action === "apply" ? "apply" : "keep existing";

const ProfileSyncReview = ({ profileSync, canUpdate, loading = false, error = "", onDecision, onReload }) => {
  const sync = normalizeProfileSync(profileSync);
  const [confirmation, setConfirmation] = useState(null);
  if (!sync.fields.length && !error) return null;

  const confirm = () => {
    if (!confirmation || !onDecision) return;
    onDecision(confirmation.field, confirmation.action);
    setConfirmation(null);
  };

  return (
    <section className="mb-4" aria-label="Profile updates from consultation form">
      <h5 className="fw-semibold mb-2">Client profile updates</h5>
      {error && (
        <div className="alert alert-danger" role="alert">
          {Number(error.status) === 409
            ? "The client profile changed since this form was reviewed. Reload before choosing a value."
            : error.message || "Unable to update the client profile."}
          {Number(error.status) === 409 && onReload && (
            <button type="button" className="btn btn-link p-0 ms-2" onClick={onReload}>
              Reload profile updates
            </button>
          )}
        </div>
      )}
      {sync.fields.map((field) => {
        const pending = ["pending", "conflict"].includes(field.status);
        return (
          <div className="border rounded p-3 mb-2" key={field.field}>
            <h6 className="fw-bold">{field.label}</h6>
            <div><span className="fw-semibold">Submitted:</span> {String(field.submittedValue || "Not provided")}</div>
            {pending || field.status === "rejected" ? (
              <div><span className="fw-semibold">Current profile:</span> {String(field.currentValue || "Not set")}</div>
            ) : null}
            {field.status === "applied" && <div className="text-success mt-2">Applied to profile</div>}
            {field.status === "rejected" && <div className="text-muted mt-2">Existing value kept</div>}
            {pending && !canUpdate && (
              <div className="text-muted mt-2">You need permission to update clients before choosing a value.</div>
            )}
            {pending && canUpdate && (
              <div className="mt-2 d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={loading}
                  aria-label={`Apply submitted ${field.label}`}
                  onClick={() => setConfirmation({ field: field.field, label: field.label, action: "apply" })}
                >
                  Apply submitted
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={loading}
                  aria-label={`Keep existing ${field.label}`}
                  onClick={() => setConfirmation({ field: field.field, label: field.label, action: "reject" })}
                >
                  Keep existing
                </button>
              </div>
            )}
          </div>
        );
      })}
      {confirmation && (
        <div className="border rounded bg-light p-3 mt-2" role="dialog" aria-label="Confirm profile update">
          <p className="mb-2">
            Confirm {decisionLabel(confirmation.action)} for {confirmation.label}? This decision is recorded with the consultation form.
          </p>
          <button
            type="button"
            className="btn btn-primary btn-sm me-2"
            disabled={loading}
            aria-label={`Confirm ${decisionLabel(confirmation.action)}`}
            onClick={confirm}
          >
            Confirm
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" disabled={loading} onClick={() => setConfirmation(null)}>
            Cancel
          </button>
        </div>
      )}
    </section>
  );
};

ProfileSyncReview.propTypes = {
  profileSync: PropTypes.object,
  canUpdate: PropTypes.bool,
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onDecision: PropTypes.func,
  onReload: PropTypes.func,
};

export default ProfileSyncReview;

